/**
 * BuildingLoader — the Executive Report's loading state, in the brief's own
 * shape so the page does not jump when the report lands: the composer's
 * loading brief (BriefHero) with the view's primary button and period
 * control, and a vertical Carbon
 * progress indicator of what Guardian is doing, beside the Guardian Score
 * tile in its loading state (a dash and skeleton lines where the waffle
 * goes).
 *
 * The report's backend generation (the AI narrative plus score aggregation)
 * is synchronous and takes about 10 to 30 seconds, with no progress feed, so
 * the steps are timer-driven and say what Guardian would be doing without
 * claiming a percentage. The last step stays current until the data
 * resolves; the page swaps the loader out at that point.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  BriefHero,
  ProgressIndicator,
  ScoreTile,
  Skeleton,
  type BriefHeroButtonAction,
  type StepState,
} from '@/components/carbon'
import { composeExecutiveReportBrief } from '@/lib/executive-report-brief'

interface Stage {
  /** Stable id for React keying. */
  id: string
  /** The step's label. */
  label: string
  /** Helper line shown under the current step. */
  detail: string
  /** Milliseconds before the loader advances past this stage. The last stage's dwell never elapses in practice. */
  dwellMs: number
}

const STAGES: ReadonlyArray<Stage> = [
  {
    id: 'gather',
    label: 'Gathering your scores',
    detail: 'Pulling the Secure Score, Best Practice, MFA and baseline scores together.',
    dwellMs: 2400,
  },
  {
    id: 'review',
    label: 'Reviewing the period',
    detail: 'Reading the period’s score history, drift, framework controls and waivers.',
    dwellMs: 3200,
  },
  {
    id: 'draft',
    label: 'Writing the board summary',
    detail: 'Composing the summary from the period’s data.',
    // Sticky: the page swaps the loader out when the report query resolves.
    dwellMs: 60_000,
  },
]

interface Props {
  /** Reporting window in days. */
  periodDays?: number
  /** The view's primary button (Download PDF, disabled until the report lands), so it stays in place. */
  primaryAction?: BriefHeroButtonAction
  /** The period select: BriefHero keeps view controls while the brief loads, so the period can still change. */
  controls?: ReactNode
  /** Deprecated: free-form actions, rendered as BriefHero's tertiary slot. Use `primaryAction` and `controls`. */
  actions?: ReactNode
}

export default function BuildingLoader({ periodDays, primaryAction, controls, actions }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    // Walk through STAGES on a chained timer. The last stage's timer never
    // fires in practice because the page unmounts the loader once data arrives.
    if (activeIndex >= STAGES.length - 1) return
    const stage = STAGES[activeIndex]
    const t = window.setTimeout(() => {
      setActiveIndex(i => Math.min(i + 1, STAGES.length - 1))
    }, stage.dwellMs)
    return () => window.clearTimeout(t)
  }, [activeIndex])

  // The composer's own loading brief ("Compiling the report."), so the loader and the report share one voice.
  const brief = useMemo(() => composeExecutiveReportBrief({ periodDays, score: null }), [periodDays])

  const steps = STAGES.map((stage, idx) => {
    const state: StepState = idx < activeIndex ? 'complete' : idx === activeIndex ? 'current' : 'incomplete'
    return { id: stage.id, label: stage.label, state, helper: state === 'current' ? stage.detail : undefined }
  })

  return (
    <section
      aria-live="polite"
      aria-busy="true"
      aria-labelledby="exec-building-headline"
      className="mt-8 grid grid-cols-1 items-stretch gap-8 c-xlg:grid-cols-[minmax(0,1fr)_minmax(0,560px)]"
    >
      <div className="min-w-0">
        <BriefHero
          brief={brief}
          size="hero"
          headingId="exec-building-headline"
          primaryAction={primaryAction}
          secondaryAction={actions}
          controls={controls}
        />
        <p className="m-0 mt-8 max-w-[56ch] text-helper text-text-helper">
          {typeof periodDays === 'number' ? `Reading ${periodDays} days of assessments. ` : ''}The board summary is written
          live, so this can take ten to thirty seconds.
        </p>
        <ProgressIndicator vertical aria-label="Report progress" steps={steps} className="mt-6" />
      </div>

      <ScoreTile
        size="hero"
        title="Guardian Score"
        score={null}
        isLoading
        aside={<Skeleton lines={4} width="158px" />}
        className="max-c-xlg:max-w-[640px]"
      />
    </section>
  )
}
